/**
 * 阿里云验证码2.0配置
 * 文档: https://help.aliyun.com/zh/captcha/captcha2-0/user-guide/new-architecture-for-web-and-h5-client-access
 */

// 阿里云验证码全局配置（写死配置）
window.AliyunCaptchaConfig = {
  // 验证码实例所属地域，支持中国内地(cn)、新加坡(sgp)
  region: 'cn',
  // 身份标识(prefix)，在阿里云验证码控制台概览页面获取
  prefix: '1icjgt'
};

// 本地配置
const localConfig = {
  prefix: window.AliyunCaptchaConfig.prefix,
  sceneId: 'vptf09bd' // 场景ID，在阿里云验证码控制台获取
};

let captchaInstance = null;
let captchaCallbacks = {
  success: null,
  fail: null
};

/**
 * 根据屏幕宽度计算自适应 rem 值
 * 用于整体缩放验证码UI，实现PC端和移动端自适应
 * @returns {number} rem 值，0.5缩小一倍，1正常，2放大一倍
 */
function getAdaptiveRem() {
  const baseWidth = 375; // 基准宽度（移动端设计稿宽度）
  const minRem = 0.6;    // 最小 rem 值
  const maxRem = 0.9;   // 最大 rem 值

  // 获取屏幕宽度（兼容移动端）
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

  // 根据屏幕宽度计算 rem 值
  let rem = screenWidth / baseWidth;

  // 限制范围
  rem = Math.max(minRem, Math.min(maxRem, rem));

  return rem;
}

/**
 * 动态加载阿里云验证码JS
 * @returns {Promise}
 */
function loadAliyunCaptchaScript() {
  return new Promise((resolve, reject) => {
    // 检查是否已加载
    if (window.initAliyunCaptcha) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('阿里云验证码JS加载失败'));
    document.head.appendChild(script);
  });
}

/**
 * 获取验证码配置（本地写死的配置）
 * @returns {Object} 配置对象 { prefix, sceneId }
 */
export function getCaptchaSettings() {
  return {
    prefix: localConfig.prefix,
    sceneId: localConfig.sceneId
  };
}

/**
 * 初始化阿里云验证码
 * @param {Object} options 初始化选项
 * @param {string} options.SceneId 场景ID
 * @param {string} options.element 验证码元素选择器
 * @param {string} options.button 触发按钮选择器
 * @param {Function} options.onSuccess 验证成功回调
 * @param {Function} options.onFail 验证失败回调
 * @param {Function} options.onError 错误回调
 * @param {Function} options.onClose 关闭回调
 * @returns {Promise}
 */
export async function initAliyunCaptcha(options = {}) {
  try {
    // 获取配置
    const config = getCaptchaSettings();

    // 加载JS脚本
    await loadAliyunCaptchaScript();

    // 注入自定义样式
    injectCaptchaStyles();

    // 保存回调
    captchaCallbacks.success = options.onSuccess;
    captchaCallbacks.fail = options.onFail;

    // 返回Promise，等待实例准备好
    return new Promise((resolve, reject) => {
      // 设置超时
      const timeout = setTimeout(() => {
        reject(new Error('验证码初始化超时'));
      }, 10000);

      window.initAliyunCaptcha({
        SceneId: options.SceneId || config.sceneId,
        mode: 'popup',
        element: options.element || '#aliyun-captcha-element',
        // popup 模式下 button 参数可选，不传则完全由代码控制显示/隐藏
        ...(options.button && { button: options.button }),
        slideStyle: {
          width: 320,
          height: 40
        },
        // rem: 整体缩放比例，0.5缩小一倍，2放大一倍，用于UI自适应
        rem: getAdaptiveRem(),
        disableMaskClick: true,
        language: 'cn',
        timeout: 10000,
        // 验证成功回调
        success: function(captchaVerifyParam) {
          // console.log('阿里云验证码验证成功', captchaVerifyParam);
          if (captchaCallbacks.success) {
            captchaCallbacks.success(captchaVerifyParam);
          }
        },
        // 验证失败回调
        fail: function(result) {
          console.log('阿里云验证码验证失败', result);
          if (captchaCallbacks.fail) {
            captchaCallbacks.fail(result);
          }
        },
        // 获取验证码实例 - 关键：实例准备好后再resolve
        getInstance: function(instance) {
          clearTimeout(timeout);
          captchaInstance = instance;
          console.log('阿里云验证码实例已获取');
          resolve(instance);
        },
        // 错误回调
        onError: function(errorInfo) {
          clearTimeout(timeout);
          const { code, msg } = errorInfo;
          console.error('阿里云验证码初始化错误', code, msg);
          reject(errorInfo);
        },
        // 关闭回调
        onClose: function() {
          console.log('阿里云验证码弹窗已关闭');
          // 手动关闭后清空实例，下次打开时会重新初始化
          captchaInstance = null;
          if (options.onClose) {
            options.onClose();
          }
        }
      });
    });
  } catch (error) {
    console.error('阿里云验证码初始化失败', error);
    throw error;
  }
}

/**
 * 显示验证码弹窗
 */
export function showCaptcha() {
  if (captchaInstance) {
    captchaInstance.show();
  } else {
    console.warn('验证码实例尚未初始化');
  }
}

/**
 * 隐藏验证码弹窗
 */
export function hideCaptcha() {
  if (captchaInstance) {
    captchaInstance.hide();
  }
}

/**
 * 注入阿里云验证码自定义样式
 */
function injectCaptchaStyles() {
  const styleId = 'aliyun-captcha-custom-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .aliyunCaptcha-top #aliyunCaptcha-title {
      font-size: 16px !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * 获取验证码实例
 * @returns {Object} captchaInstance
 */
export function getCaptchaInstance() {
  return captchaInstance;
}

/**
 * 重置验证码
 */
export function resetCaptcha() {
  if (captchaInstance) {
    captchaInstance.hide();
    captchaInstance = null;
  }
}

export default {
  initAliyunCaptcha,
  showCaptcha,
  hideCaptcha,
  getCaptchaInstance,
  resetCaptcha,
  loadAliyunCaptchaScript,
  getCaptchaSettings
};
